import React from "react";
import { ShoppingCart, ArrowUpRight, ExternalLink, Youtube, Headphones, Clock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { purchaseDetails } from "@/lib/purchases";

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const purchase = purchaseDetails(product);
  const link = purchase.url || product.link;
  const hasLink = Boolean(link);
  const comingSoon = Boolean(product.coming_soon) && !purchase.external;
  const isYoutube = hasLink && /youtu\.?be/i.test(link);
  const isAmazon = hasLink && /amazon|amzn/i.test(link);
  const linkLabel = isYoutube ? "Écouter sur YouTube" : isAmazon ? "Acheter sur Amazon" : purchase.external ? purchase.label : "Acheter le livre audio";
  const LinkIcon = isYoutube ? Youtube : isAmazon ? ExternalLink : Headphones;
  const isFree = !comingSoon && Number(product.price) === 0;
  const containImage = ["Audio", "Ebook"].includes(product.format);

  const handleAdd = () => {
    addItem(product);
    toast.success("Ajouté au panier", { description: product.name });
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col bg-white rounded-xl overflow-hidden border border-transparent hover:border-gold transition-all duration-300 hover:-translate-y-1"
      style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.05)" }}
    >
      <div className={`relative aspect-[4/3] overflow-hidden ${containImage ? "bg-[#0a0e1a]" : ""}`}>
        <img
          src={product.image}
          alt={product.name}
          className={`w-full h-full ${containImage ? "object-contain" : "object-cover"} transition-transform duration-700 group-hover:scale-105 ${comingSoon ? "opacity-95" : ""}`}
        />
        {!product.format && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-ivory/90 backdrop-blur-sm text-[11px] font-semibold text-brown-dark border border-gold/30">
            {product.category}
          </span>
        )}
        {product.format && (
          <span className="absolute bottom-3 left-3 inline-flex items-center px-3 py-1 rounded-full bg-gold text-white text-[11px] font-bold uppercase tracking-wider shadow-md" data-testid={`product-format-${product.id}`}>
            {product.format}
          </span>
        )}
        {comingSoon && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brown-dark/90 backdrop-blur-sm text-[11px] font-semibold text-gold-light border border-gold/40">
            <Clock size={12} /> Arrive prochainement
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-heading text-xl font-medium text-brown-black leading-snug">{product.name}</h3>
        <p className="text-sm text-brown-black/60 mt-1.5 leading-relaxed flex-1">{product.description}</p>
        <div className="flex items-center justify-between mt-5">
          {comingSoon ? (
            <span className="font-heading text-lg font-semibold text-brown-dark/60 italic">Bientôt disponible</span>
          ) : isFree ? (
            <span className="font-heading text-2xl font-semibold text-gold">Gratuit</span>
          ) : (
            <span className="font-heading text-2xl font-semibold text-gold">{product.price.toFixed(2)} €</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-4">
          {comingSoon || (!hasLink && !purchase.onsite) ? (
            <button
              disabled
              data-testid={`coming-soon-${product.id}`}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-brown-dark/10 text-brown-dark/50 text-sm font-semibold cursor-not-allowed"
            >
              <Clock size={15} /> Arrive prochainement
            </button>
          ) : hasLink ? (
            <>
              <a
                href={link} target="_blank" rel="noopener noreferrer"
                data-testid={`buy-link-${product.id}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gold text-white text-sm font-semibold hover:bg-gold-light transition-colors duration-300"
              >
                <LinkIcon size={15} /> {linkLabel}
              </a>
              <a
                data-testid={`discover-${product.id}`}
                href={link} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300"
                aria-label="Découvrir"
              >
                <ArrowUpRight size={18} />
              </a>
            </>
          ) : (
            <>
              <button
                onClick={handleAdd}
                data-testid={`add-to-cart-${product.id}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gold text-white text-sm font-semibold hover:bg-gold-light transition-colors duration-300"
              >
                <ShoppingCart size={15} /> Ajouter au panier
              </button>
              <button
                data-testid={`discover-${product.id}`}
                onClick={handleAdd}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300"
                aria-label="Découvrir"
              >
                <ArrowUpRight size={18} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
